
import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";
import { toast } from "@/components/ui/use-toast";

const AppointmentAction = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    const processAction = async () => {
      try {
        const token = searchParams.get("token");
        const action = searchParams.get("action");

        if (!token || !action) {
          throw new Error("Paramètres manquants dans l'URL");
        }

        const mapError = (code: string) => {
          if (code === "used") return "Ce lien a déjà été utilisé";
          if (code === "expired") return "Ce lien a expiré";
          return "Lien invalide ou expiré";
        };

        // Effectuer l'action de façon sécurisée et atomique (SECURITY DEFINER)
        if (action === "confirm") {
          const { data, error } = await supabase.rpc("confirm_appointment_by_token", {
            p_token: token,
          });
          if (error) throw new Error("Une erreur s'est produite");
          const appointment: any = data;
          if (!appointment || appointment.error) {
            throw new Error(mapError(appointment?.error));
          }

          // Envoyer l'email de confirmation au client
          await supabase.functions.invoke("send-email", {
            body: {
              type: "booking_user_status",
              data: {
                client_name: appointment.client_name,
                client_email: appointment.client_email,
                date: appointment.date,
                start_time: appointment.start_time.substring(0, 5),
                end_time: appointment.end_time.substring(0, 5),
                services: (appointment.appointment_services || []).map((item: any) => ({
                  name: item.services.name,
                  price: item.services.price,
                })),
                status: "confirmed",
                cancel_token: appointment.cancel_token,
              },
            },
          });

          toast({
            title: "Rendez-vous confirmé",
            description: "Le rendez-vous a été confirmé avec succès",
          });
        } else if (action === "cancel") {
          const { data, error } = await supabase.rpc("cancel_appointment_by_token", {
            p_token: token,
            p_reason_type: "admin",
            p_reason: "Annulé par l'institut",
          });
          if (error) throw new Error("Une erreur s'est produite");
          const appointment: any = data;
          if (!appointment || appointment.error) {
            throw new Error(mapError(appointment?.error));
          }

          // Envoyer l'email d'annulation au client
          await supabase.functions.invoke("send-email", {
            body: {
              type: "booking_user_status",
              data: {
                client_name: appointment.client_name,
                client_email: appointment.client_email,
                date: appointment.date,
                start_time: appointment.start_time.substring(0, 5),
                end_time: appointment.end_time.substring(0, 5),
                services: (appointment.appointment_services || []).map((item: any) => ({
                  name: item.services.name,
                  price: item.services.price,
                })),
                status: "cancelled",
                cancellation_reason: "Annulé par l'institut",
                cancelled_by: "admin",
              },
            },
          });

          toast({
            title: "Rendez-vous annulé",
            description: "Le rendez-vous a été annulé avec succès",
          });
        } else {
          throw new Error("Action non reconnue");
        }

        // Rediriger vers la page admin
        setTimeout(() => {
          navigate("/admin/calendar");
        }, 2000);
      } catch (error: any) {
        console.error("Erreur lors du traitement de l'action:", error);
        setErrorMessage(error.message || "Une erreur s'est produite");
      } finally {
        setIsProcessing(false);
      }
    };

    processAction();
  }, [searchParams, navigate]);

  return (
    <div className="pt-24 pb-16 px-4">
      <div className="container mx-auto max-w-md">
        <div className="bg-white rounded-lg shadow-md p-8 text-center">
          {isProcessing ? (
            <>
              <Loader2 className="h-12 w-12 animate-spin text-bbz-green mx-auto mb-4" />
              <h2 className="text-2xl font-semibold mb-2">
                Traitement en cours...
              </h2>
              <p className="text-bbz-text/70">
                Veuillez patienter pendant que nous traitons votre action
              </p>
            </>
          ) : errorMessage ? (
            <>
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                <span className="text-red-500 text-2xl">×</span>
              </div>
              <h2 className="text-2xl font-semibold mb-2">Erreur</h2>
              <p className="text-bbz-text/70 mb-4">{errorMessage}</p>
              <button
                onClick={() => navigate("/")}
                className="px-4 py-2 bg-bbz-green text-white rounded-md hover:bg-bbz-green/80 transition-colors"
              >
                Retourner à l'accueil
              </button>
            </>
          ) : (
            <>
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                <span className="text-green-500 text-2xl">✓</span>
              </div>
              <h2 className="text-2xl font-semibold mb-2">Action effectuée</h2>
              <p className="text-bbz-text/70 mb-4">
                Votre action a été traitée avec succès. Vous allez être redirigé vers
                le tableau de bord.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AppointmentAction;
